import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { productSlice } from "../modules/products/reducer";
import { currentDateSelector } from "../modules/products/selectors";
import { orderDateFromToday } from "../utils/format";
import { LinkButton } from "../styles";
import SelectionTiles from "./selectionTiles";
import Collapse from "./collapse";
import { DatePickerHeader, DatePickerScroller } from "./styles";

const weekdayNames = {
  0: "ND",
  1: "PON",
  2: "WT",
  3: "ŚR",
  4: "CZW",
  5: "PT",
  6: "SOB",
};

const relativeNames = ["dziś", "jutro"];
const monthNames = [
  "sty",
  "lut",
  "mar",
  "kwi",
  "maj",
  "cze",
  "lip",
  "sie",
  "wrz",
  "paź",
  "lis",
  "gru",
];
const fullWeekdayNames = {
  0: "niedzielę",
  1: "poniedziałek",
  2: "wtorek",
  3: "środę",
  4: "czwartek",
  5: "piątek",
  6: "sobotę",
};
const fullMonthNames = [
  "stycznia",
  "lutego",
  "marca",
  "kwietnia",
  "maja",
  "czerwca",
  "lipca",
  "sierpnia",
  "września",
  "października",
  "listopada",
  "grudnia",
];

function DatePicker() {
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();
  const currentDate = useSelector(currentDateSelector);
  const dailyMenuEnabled = useSelector((state) => state.store.dailyMenuEnabled);
  const daysOffset =
    useSelector((state) => state.store.openingHoursDaysOffset) ?? 0;
  const days = [...Array(14)].map((_, i) => {
    // Calendar arithmetic in UTC keeps each step at one day across DST changes.
    const offset = daysOffset + i;
    const isoFormat = orderDateFromToday(offset);
    const date = new Date(`${isoFormat}T12:00:00Z`);

    return {
      value: isoFormat,
      isoFormat,
      active: currentDate === isoFormat,
      weekday: weekdayNames[date.getUTCDay()],
      dayNumber: date.getUTCDate(),
      meta: relativeNames[offset] ?? monthNames[date.getUTCMonth()],
      summary: `${fullWeekdayNames[date.getUTCDay()]}, ${date.getUTCDate()} ${
        fullMonthNames[date.getUTCMonth()]
      }`,
    };
  });
  const selectedDay = days.find((day) => day.active) || days[0];

  function selectDate(isoFormat) {
    dispatch(productSlice.actions.setDate(isoFormat));
    setExpanded(false);
  }

  if (!dailyMenuEnabled && daysOffset === 0) {
    return (
      <div>
        <DatePickerHeader>
          <div>
            <span className="interface-label">Oferta restauracji</span>
            <strong>Menu</strong>
          </div>
        </DatePickerHeader>
      </div>
    );
  }

  return (
    <div>
      <DatePickerHeader>
        <div>
          <span className="interface-label">Wybrany dzień</span>
          <strong>Menu na {selectedDay.summary}</strong>
          {daysOffset > 0 && (
            <small className="date-picker-hint">
              Zamówienia przyjmujemy z wyprzedzeniem minimum {daysOffset}{" "}
              {daysOffset === 1 ? "dnia" : "dni"}.
            </small>
          )}
        </div>
        <LinkButton
          className="date-picker-toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls="date-picker-options"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Zamknij" : "Zmień dzień"}
        </LinkButton>
      </DatePickerHeader>
      <Collapse
        id="date-picker-options"
        expanded={expanded}
        innerClassName="date-picker-collapse-inner"
      >
        <DatePickerScroller>
          <SelectionTiles
            ariaLabel="Wybierz dzień menu"
            fill
            options={days.map((day) => ({
              ...day,
              ariaLabel: `${day.weekday}, ${day.dayNumber} ${day.meta}`,
            }))}
            value={currentDate}
            onChange={selectDate}
            renderOption={({ weekday, dayNumber, meta }) => (
              <>
                <span className="date-weekday">{weekday}</span>
                <span className="date-number">{dayNumber}</span>
                <span className="date-meta">{meta}</span>
              </>
            )}
          />
        </DatePickerScroller>
      </Collapse>
    </div>
  );
}

export default DatePicker;
